import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, ShoppingCart, Star } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { calculateDiscountPercent } from "../lib/pricing";

const FREE_SHIPPING_MIN = 200000;
const WHATSAPP_LINK_BASE = "https://wa.me/573184215616";

const parseColorOptions = (colors) => {
  if (!colors) return [];
  const raw = Array.isArray(colors) ? colors : String(colors).split(",");
  const seen = new Set();
  const values = [];
  raw.forEach((item) => {
    const cleaned = String(item || "").trim();
    if (!cleaned) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    values.push(cleaned);
  });
  return values;
};

const ProductCard = ({ producto, showWhatsappLabel = true }) => {
  const navigate = useNavigate();
  const formatoPrecio = (precio) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(precio);
  };

  const nombre = producto.name || producto.nombre;
  const marca = producto.brand || producto.marca;
  const imagen = producto.image_url || producto.imagen;
  const precio = producto.price ?? producto.precio ?? 0;
  const precioOferta = producto.sale_price ?? producto.precioOferta;
  const effectivePrice = Number(precioOferta ?? precio ?? 0);
  const qualifiesFreeShipping = effectivePrice >= FREE_SHIPPING_MIN;
  const isWhatsappOnly = Boolean(producto.whatsapp_only);
  const discountPercent = calculateDiscountPercent(precio, precioOferta);
  const availableStock = Number(producto.stock ?? 0);

  const openWhatsappMessage = (message) => {
    window.open(
      `${WHATSAPP_LINK_BASE}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const buildCreditMessage = () =>
    [
      "Hola, quiero mi credito. Estoy interesado en este producto:",
      nombre,
      `Foto: ${imagen}`,
    ].join("\n");

  const parseSize = (value) => {
    if (!value) return { raw: "", us: "", eu: null };
    const text = String(value).trim();
    const usMatch = text.match(/US\s*([0-9./]+)/i);
    const euMatch = text.match(/EUR\s*([0-9./]+)/i);
    return {
      raw: text,
      us: usMatch ? usMatch[1] : text,
      eu: euMatch ? euMatch[1] : null,
    };
  };

  const handleQuickCart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (availableStock <= 0) return;

    if (isWhatsappOnly) {
      const message = [
        "Hola, quiero comprar por WhatsApp:",
        nombre,
        "Cantidad: 1",
        `Foto: ${imagen}`,
      ].join("\n");
      openWhatsappMessage(message);
      return;
    }

    const firstSize = Array.isArray(producto.sizes) && producto.sizes.length
      ? parseSize(producto.sizes[0])
      : null;
    const defaultColor = parseColorOptions(producto.color)[0] || null;
    const item = {
      productoId: producto.id,
      nombre,
      imagen,
      precio: Number(precioOferta ?? precio ?? 0),
      cantidad: 1,
      talla: producto.category === "accesorios" ? null : firstSize?.raw || firstSize?.us || null,
      tallaEu: firstSize?.eu || null,
      color: defaultColor,
    };
    const carrito = JSON.parse(localStorage.getItem("carrito") || "[]");
    const existingIndex = carrito.findIndex(
      (entry) =>
        entry.productoId === item.productoId &&
        entry.talla === item.talla &&
        (entry.color || null) === (item.color || null),
    );
    if (existingIndex >= 0) {
      carrito[existingIndex].cantidad += 1;
    } else {
      carrito.push(item);
    }
    localStorage.setItem("carrito", JSON.stringify(carrito));
    navigate("/checkout");
  };

  const handleCreditInterest = (event) => {
    event.preventDefault();
    event.stopPropagation();
    openWhatsappMessage(buildCreditMessage());
  };

  return (
    <div className="relative">
      <Link to={`/producto/${producto.id}`}>
        <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300">
        <CardContent className="p-0">
          {/* Imagen */}
          <div className="relative overflow-hidden aspect-square">
            <img
              src={imagen}
              alt={nombre}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {precioOferta && (
              <Badge className="absolute top-2 left-2 bg-red-600 text-white">
                {discountPercent > 0 ? `-${discountPercent}%` : "Oferta"}
              </Badge>
            )}
            <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
              <Button
                type="button"
                onClick={handleCreditInterest}
                className="h-9 rounded-full bg-emerald-600/95 px-3 text-xs font-semibold text-white shadow-lg hover:bg-emerald-700"
                title="Quiero mi credito"
              >
                <CreditCard className="mr-1 h-4 w-4" />
                Mi credito
              </Button>
              <Button
                type="button"
                size="icon"
                onClick={handleQuickCart}
                disabled={availableStock <= 0}
                className="h-9 w-9 rounded-full bg-black/90 text-white shadow-lg hover:bg-black"
                title={isWhatsappOnly ? "Asesoria y Compra por WhatsApp" : "Comprar ahora"}
              >
                <ShoppingCart className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-4">
            {marca && <p className="text-xs text-gray-500 mb-1">{marca}</p>}
            <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-gray-600 transition-colors">
              {nombre}
            </h3>

            {/* Rating */}
            {producto.rating && (
              <div className="flex items-center gap-1 mb-2">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-xs text-gray-600">
                  {producto.rating} ({producto.reviews})
                </span>
              </div>
            )}

            {/* Precio */}
            <div className="flex items-center gap-2">
              {precioOferta ? (
                <>
                  <span className="text-gray-400 line-through text-sm">
                    {formatoPrecio(precio)}
                  </span>
                  <span className="text-lg font-bold text-red-600">
                    {formatoPrecio(precioOferta)}
                  </span>
                  {discountPercent > 0 && (
                    <span className="text-xs font-bold text-red-600">
                      -{discountPercent}%
                    </span>
                  )}
                </>
              ) : (
                <span className="text-lg font-bold">{formatoPrecio(precio)}</span>
              )}
            </div>
            {qualifiesFreeShipping && (
              <p className="mt-2 text-xs font-semibold text-emerald-600">
                Envio gratis
              </p>
            )}
            {showWhatsappLabel && isWhatsappOnly && (
              <p className="mt-1 text-xs font-semibold text-emerald-700">
                Solo WhatsApp
              </p>
            )}
          </div>
        </CardContent>
        </Card>
      </Link>
      <Button
        type="button"
        onClick={handleQuickCart}
        disabled={availableStock <= 0}
        className="mt-3 w-full bg-black text-white hover:bg-zinc-900"
      >
        Comprar ya
      </Button>
    </div>
  );
};

export default ProductCard;
